<template>
  <div>
    <h3 class="text-xl font-bold text-neutral-900">{{ $t('Spam Settings') }}</h3>
    <p class="text-neutral-600 mt-1">{{ $t('Protect your project from spam and abuse.') }}</p>
    <form @submit.prevent="saveSettings">
    <div class="mt-6">
      <h4 class="font-semibold text-neutral-900">{{ $t('Strictness') }}</h4>
      <p class="text-neutral-600 mt-1">
        {{ $t('Choose how strict you want to be when it comes to labeling content as spam.') }}
      </p>
      <div class="grid grid-cols-1 gap-4 mt-4">
        <label
          :class="[
            'flex items-center gap-4 p-4 border-2 rounded-xl transition-all duration-200',
            optOutAllowed ? 'cursor-pointer' : 'cursor-not-allowed opacity-60',
            { 'border-[var(--accent-color)]': strictness === 'none' },
          ]"
        >
          <input
            type="radio"
            name="strictness"
            value="none"
            v-model="strictness"
            :disabled="!optOutAllowed"
            class="hidden"
          />
          <div
            class="flex items-center justify-center bg-gray-100 rounded-full p-1 w-8 h-8"
          >
            <div
              class="w-6 h-6 rounded-full"
              :class="strictness === 'none' ? 'bg-gray-500' : 'bg-gray-300'"
            ></div>
          </div>
          <div>
            <h5 class="font-bold text-neutral-800">{{ $t('None') }}</h5>
            <p class="text-sm text-neutral-700">
              {{ $t('No filtering, all content is allowed') }}
            </p>
            <p v-if="!optOutAllowed" class="text-sm text-neutral-800 mt-1">
              {{ $t('Available on paid plans.') }}
              <a href="/edit?view=billing" class="link">{{ $t('Upgrade your plan') }}</a>
            </p>
          </div>
        </label>
        <label
          :class="[
            'flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all duration-200',
            { 'border-[var(--accent-color)]': strictness === 'low' },
          ]"
        >
          <input
            type="radio"
            name="strictness"
            value="low"
            v-model="strictness"
            class="hidden"
          />
          <div
            class="flex items-center justify-center bg-gray-100 rounded-full p-1 w-8 h-8"
          >
            <div
              class="w-6 h-6 rounded-full"
              :class="strictness === 'low' ? 'bg-emerald-500' : 'bg-gray-300'"
            ></div>
          </div>
          <div>
            <h5 class="font-bold text-neutral-800">{{ $t('Low') }}</h5>
            <p class="text-sm text-neutral-700">
              {{ $t('Minimal filtering, allows most content through') }}
            </p>
          </div>
        </label>
        <label
          :class="[
            'flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all duration-200',
            { 'border-[var(--accent-color)]': strictness === 'medium' },
          ]"
        >
          <input
            type="radio"
            name="strictness"
            value="medium"
            v-model="strictness"
            class="hidden"
          />
          <div
            class="flex items-center justify-center bg-gray-100 rounded-full p-1 w-8 h-8"
          >
            <div
              class="w-6 h-6 rounded-full"
              :class="strictness === 'medium' ? 'bg-yellow-400' : 'bg-gray-300'"
            ></div>
          </div>
          <div>
            <h5 class="font-bold text-neutral-800">{{ $t('Medium') }}</h5>
            <p class="text-sm text-neutral-700">
              {{ $t('Balanced filtering, suitable for most use cases') }}
            </p>
          </div>
        </label>
        <label
          :class="[
            'flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all duration-200',
            { 'border-[var(--accent-color)]': strictness === 'high' },
          ]"
        >
          <input
            type="radio"
            name="strictness"
            value="high"
            v-model="strictness"
            class="hidden"
          />
          <div
            class="flex items-center justify-center bg-gray-100 rounded-full p-1 w-8 h-8"
          >
            <div
              class="w-6 h-6 rounded-full"
              :class="strictness === 'high' ? 'bg-rose-500' : 'bg-gray-300'"
            ></div>
          </div>
          <div>
            <h5 class="font-bold text-neutral-800">{{ $t('High') }}</h5>
            <p class="text-sm text-neutral-700">
              {{ $t('Aggressive filtering, may block some legitimate content') }}
            </p>
          </div>
        </label>
      </div>
    </div>
    
    <div class="mt-8">
      <h4 class="font-semibold text-neutral-900">{{ $t('Spam Keywords') }}</h4>
      <p class="text-neutral-600 mt-1">
        {{ $t('Add keywords or phrases that should be automatically flagged as spam.') }}
        {{ $t('We start every project with common spam words. Remove any that your customers use.') }}
      </p>
      <div class="mt-4">
        <textarea
          v-model="spam_keywords"
          rows="4"
          class="input h-40"
          spellcheck="false"
          :placeholder="$t('Enter keywords or phrases separated by commas (e.g. spam, scam, fake)')"
        ></textarea>
      </div>
    </div>

    <p v-if="errorMessage" class="alert alert--bad rounded-xl mt-6">{{ errorMessage }}</p>

    <div class="mt-8">
      <button type="submit" class="btn-main">{{ $t('Save') }}</button>
    </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  props: {
    token: String,
    project: Object
  },
  data() {
    return {
      strictness: this.project.spam_strictness,
      spam_keywords: this.project.spam_keywords.join(', '),
      errorMessage: "",
    };
  },
  computed: {
    ...mapGetters(["getCurrentProjectPlan"]),
    // Mirrors Project#spam_opt_out_allowed? (subscription_valid_strict?):
    // paid or lifetime only, never trial.
    optOutAllowed() {
      const plan = this.getCurrentProjectPlan;
      return !!(plan && plan.name !== "trial" && plan.active_status);
    },
  },
  created() {
    // A legacy "none" on an unpaid project is ignored server-side; show the
    // value that is actually in effect so Save doesn't resubmit "none".
    if (this.strictness === "none" && !this.optOutAllowed) this.strictness = "low";
  },
  methods: {
    saveSettings() {
      this.errorMessage = "";
      let data = new FormData();
      data.append("spam_strictness", this.strictness);
      data.append("spam_keywords", this.spam_keywords);
      data.append("project_id", this.project.id);

      Rails.ajax({
        url: "/projects/spam_settings",
        type: "PATCH",
        data: data,
        dataType: "json",
        beforeSend: function () {
          return true;
        },
        success: (result) => {
          if (result.message) {
            window.location.reload();
          } else if (result.errors) {
            this.errorMessage = result.errors.join(" ");
          }
        },
        error: (data, status) => {
          this.errorMessage = (data && data.errors) ? data.errors.join(" ") : this.$t("Could not save spam settings. Please try again.");
        },
      });
    },
  },
};
</script>
